// @flow

import React from 'react';
import type { Node } from 'react';
import styled, { css } from 'styled-components';
import type { AllImages } from 'hooks/use-images';
import { useImages } from 'hooks';
import { ImageLoader } from 'components/Gui';
import { flex, h1520, topPadding, Width, widths } from 'styles';

const Wrapper = styled.section`
  ${topPadding(80, 58, 84)};

  h2 {
    font-size: 2.4rem;
    text-align: center;

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      white-space: unset;
      font-size: 2.8rem;
    }
  }
`;

const List = styled.div`
  margin-top: 40px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 24px;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 50px;
    ${flex('space-between', 'flex-start')};
    flex-wrap: wrap;
  }

  > div {
    ${flex('space-between', 'flex-start')};
    max-width: 450px;
    margin: 0 auto 30px;

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      flex: 0 0 auto;
      width: calc(90% / 2);
      max-width: unset;
      margin: 0 0 50px 0;
    }

    h3 {
      ${h1520}
      margin-bottom: 16px;
    }
  }
`;

const Image = styled.div`
  ${({ imageWidth }) =>
    Array.isArray(imageWidth)
      ? css`
          ${widths(...imageWidth)};
          margin-right: 12px;

          @media print, screen and (min-width: ${Width.BREAK_MD}px) {
            margin-right: 38px;
          }
        `
      : css`
          width: ${imageWidth}px;
        `};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const DisclaimerContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

type Props = {
  title: Node,
  items: {
    image: {
      name: string,
      alt: string,
    },
    text: Node,
  }[],
  imageWidth: number | number[],
  images: AllImages,
  buttons: Node,
  disclaimer?: Node | string,
};

const FourItemImageBlurbList = ({ title, items, imageWidth, images, buttons, disclaimer, ...rest }: Props) => {
  const getImage = useImages(images);
  return (
    <Wrapper {...rest}>
      {title}
      <List role="list">
        {items.map(({ image: { name, alt }, text }, index) => (
          <div key={`ImageBlurbList-${index}`} role="listitem">
            <div>
              <Image imageWidth={imageWidth}>
                <ImageLoader image={getImage(name)} alt={alt} />
              </Image>
            </div>
            <div>{text}</div>
          </div>
        ))}
      </List>
      <ButtonContainer>{buttons}</ButtonContainer>
      <DisclaimerContainer>{disclaimer}</DisclaimerContainer>
    </Wrapper>
  );
};

export default FourItemImageBlurbList;
